import { useLicenseStore } from '../store/licenseStore';
import {
  useSubscriptionStore,
  selectIsPro as selectIsSubscriptionPro,
  selectIsInGracePeriod,
} from '../store/subscriptionStore';
import type { ProFeature } from '../types';

/**
 * Hook to check if a specific Pro feature is available.
 *
 * @param feature The Pro feature to check
 * @returns Object with feature access info and upgrade action
 *
 * @example
 * ```tsx
 * function SessionManager() {
 *   const { canUse, isPro } = useProFeature('session-management');
 *
 *   if (!canUse) {
 *     return <UpgradePrompt feature="session-management" />;
 *   }
 *
 *   return <SessionManagerContent />;
 * }
 * ```
 */
export function useProFeature(feature: ProFeature) {
  const { getTrialState, isPro: isLicensePro } = useLicenseStore();

  const isSubscriptionPro = useSubscriptionStore(selectIsSubscriptionPro);
  const isInGracePeriod = useSubscriptionStore(selectIsInGracePeriod);

  const trialState = getTrialState();
  const hasProAccess = trialState === 'active' || isSubscriptionPro || isInGracePeriod || isLicensePro();

  return {
    /** Whether the user can use this specific feature */
    canUse: hasProAccess,
    /** Whether the user currently has Pro access (trial/subscription/license/dev) */
    isPro: hasProAccess,
    /** The feature being checked */
    feature,
  };
}

/**
 * Hook to get the current license mode.
 *
 * @returns Object with license mode info
 *
 * @example
 * ```tsx
 * function AppTitle() {
 *   const { mode, isPro } = useLicenseMode();
 *   return <h1>SQL Studio {isPro ? '(Pro)' : '(Personal)'}</h1>;
 * }
 * ```
 */
export function useLicenseMode() {
  const { getTrialState, isPro: isLicensePro } = useLicenseStore();

  const isSubscriptionPro = useSubscriptionStore(selectIsSubscriptionPro);
  const isInGracePeriod = useSubscriptionStore(selectIsInGracePeriod);

  const trialState = getTrialState();
  const hasProAccess = trialState === 'active' || isSubscriptionPro || isInGracePeriod || isLicensePro();

  return {
    // Mode is derived from Pro access, never from persisted state
    // This prevents expired trials from showing as 'pro' mode
    mode: hasProAccess ? 'pro' : 'personal',
    isPro: hasProAccess,
  };
}
